/**
 * DiscussionThread Component
 *
 * Renders a governance discussion thread with comments, stage indicator,
 * and real-time updates via WebSocket.
 *
 * Story FOS-4.2.5: Composite Components
 * Task 1: Copy Discussion Components
 *
 * Features:
 * - Renders comments from props or discussionStore (FOS-4.1.3)
 * - Visual distinction for agent vs human comments (AC-4.2.5.1)
 * - Retracted comments with strikethrough and marker
 * - Real-time updates via WebSocket service
 * - Typing indicators
 * - Connection status (offline banner)
 *
 * @module components/governance/DiscussionThread
 * @see AC-4.2.5.1 - DiscussionThread Component Migrated
 * @see AC-4.2.5.5 - Real-time Comments Work Correctly
 */

import { useEffect, useState } from 'react';
import { useStore } from '@nanostores/react';
import { Bot, User, AlertCircle, MessageSquare, ChevronUp, ChevronDown, Pencil, Trash2 } from 'lucide-react';
import {
  $sortedComments,
  $isDiscussionConnected,
  $typingParticipantNames,
  type RealtimeComment,
} from '../../stores/discussionStore';
import { subscribeThread, unsubscribeThread } from '../../services/discussionWebsocketService';
import { MarkdownRenderer } from '../shared/MarkdownRenderer';

/**
 * Props for DiscussionThread component
 */
export interface DiscussionThreadProps {
  /** The thread ID to display */
  threadId: string;
  /** Optional comments to display (if not provided, uses store) */
  comments?: RealtimeComment[];
  /** Optional typing users list (if not provided, uses store) */
  typingUsers?: string[];
  /** Optional connected status (if not provided, uses store) */
  isConnected?: boolean;
  /** Optional class name for styling */
  className?: string;
  /** Whether to auto-subscribe to WebSocket (default: true) */
  autoSubscribe?: boolean;
  /** User ID for WebSocket subscription (required if autoSubscribe=true) */
  userId?: string;
  /** User display name for WebSocket subscription (required if autoSubscribe=true) */
  userDisplayName?: string;
  /** Current logged-in user ID (for showing edit/delete on own comments) */
  currentUserId?: string;
  /** Callback when a comment is upvoted (1) or downvoted (-1) */
  onVoteComment?: (commentId: string, vote: 1 | -1) => Promise<void>;
  /** Callback when a comment is edited */
  onEditComment?: (commentId: string, content: string) => Promise<void>;
  /** Callback when a comment is deleted */
  onDeleteComment?: (commentId: string) => Promise<void>;
}

/**
 * Format a timestamp for display
 */
function formatTimestamp(timestamp: number): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - timestamp;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) {
    return 'Just now';
  } else if (diffMins < 60) {
    return `${diffMins}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else if (diffDays < 7) {
    return `${diffDays}d ago`;
  } else {
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  }
}

/**
 * Check if a comment is from an AI agent
 */
function isAgentComment(comment: RealtimeComment): boolean {
  return typeof comment.authorType === 'object' && 'Agent' in comment.authorType;
}

/**
 * Props for the internal comment item
 */
interface CommentItemInternalProps {
  comment: RealtimeComment;
  currentUserId?: string;
  onVoteComment?: (commentId: string, vote: 1 | -1) => Promise<void>;
  onEditComment?: (commentId: string, content: string) => Promise<void>;
  onDeleteComment?: (commentId: string) => Promise<void>;
}

/**
 * Single comment component
 */
function CommentItem({
  comment,
  currentUserId,
  onVoteComment,
  onEditComment,
  onDeleteComment,
}: CommentItemInternalProps) {
  const isAgent = isAgentComment(comment);
  const isRetracted = comment.isRetracted;
  const isOwn = currentUserId != null && comment.authorId === currentUserId;
  const [showEditForm, setShowEditForm] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);

  const handleUpvote = () => {
    onVoteComment?.(comment.id, 1);
  };

  const handleDownvote = () => {
    onVoteComment?.(comment.id, -1);
  };

  const handleEditSubmit = async () => {
    if (!editContent.trim()) return;
    setIsSubmittingEdit(true);
    try {
      await onEditComment?.(comment.id, editContent.trim());
      setShowEditForm(false);
    } finally {
      setIsSubmittingEdit(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Delete your comment? This cannot be undone.')) {
      await onDeleteComment?.(comment.id);
    }
  };

  return (
    <div
      className={`flex gap-3 p-4 rounded-lg transition-colors ${
        isRetracted
          ? 'bg-muted/30 opacity-60'
          : isAgent
            ? 'bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-800'
            : 'bg-card hover:bg-accent/50'
      }`}
      data-testid="discussion-comment"
      data-agent={isAgent.toString()}
      data-retracted={isRetracted.toString()}
    >
      {/* Avatar */}
      <div
        className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
          isAgent
            ? 'bg-purple-500 text-white'
            : 'bg-primary/10 text-primary'
        }`}
      >
        {isAgent ? (
          <Bot className="h-5 w-5" />
        ) : (
          <User className="h-5 w-5" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Header */}
        <div className="flex items-center gap-2 mb-1">
          <span className="font-medium text-sm text-foreground">
            {comment.authorName}
          </span>

          {/* AI Badge (AC-4.2.5.1) */}
          {isAgent && (
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium rounded bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300"
              data-testid="agent-badge"
            >
              <Bot className="h-3 w-3" />
              AI
            </span>
          )}

          {/* Retracted marker */}
          {isRetracted && (
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium rounded bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
              data-testid="retracted-marker"
            >
              <AlertCircle className="h-3 w-3" />
              Retracted
            </span>
          )}

          {/* Timestamp */}
          <span
            className="text-xs text-muted-foreground ml-auto"
            data-testid="comment-timestamp"
          >
            {formatTimestamp(comment.timestamp)}
          </span>
        </div>

        {/* Comment content or edit form */}
        {showEditForm ? (
          <div className="mt-1 mb-2" data-testid="discussion-edit-form">
            <textarea
              className="w-full text-sm border border-border rounded-md p-2 bg-background text-foreground resize-none focus:outline-none focus:ring-1 focus:ring-primary"
              rows={3}
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              data-testid="discussion-edit-textarea"
            />
            <div className="flex gap-2 mt-1">
              <button
                type="button"
                onClick={handleEditSubmit}
                disabled={isSubmittingEdit || !editContent.trim()}
                className="px-3 py-1 text-xs font-medium rounded bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="discussion-edit-save"
              >
                {isSubmittingEdit ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => { setShowEditForm(false); setEditContent(comment.content); }}
                className="px-3 py-1 text-xs font-medium rounded border border-border text-muted-foreground hover:text-foreground"
                data-testid="discussion-edit-cancel"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div
            className={`text-sm ${isRetracted ? 'line-through text-muted-foreground' : 'text-foreground'}`}
            data-testid="comment-content"
          >
            <MarkdownRenderer content={comment.content} />
          </div>
        )}

        {/* Vote + action buttons (only for non-retracted comments) */}
        {!isRetracted && !showEditForm && (
          <div className="flex items-center gap-3 mt-2" data-testid="comment-actions">
            {/* Vote controls — score is always visible; buttons only when voting is enabled */}
            <div className="flex items-center gap-1" data-testid="vote-controls">
              {onVoteComment && (
                <button
                  type="button"
                  onClick={handleUpvote}
                  title="Upvote"
                  className="flex items-center gap-0.5 p-0.5 rounded text-muted-foreground hover:text-primary hover:bg-muted"
                  data-testid="comment-upvote"
                >
                  <ChevronUp className="h-4 w-4" />
                  <span className="text-xs" data-testid="upvote-count">{comment.upvotes ?? 0}</span>
                </button>
              )}
              {/* BL-107 / BUG-DAO-032: Vote score always visible, even on read-only threads */}
              <span
                className="text-xs font-medium min-w-[1.5rem] text-center text-muted-foreground"
                data-testid="vote-score"
              >
                {comment.score ?? 0}
              </span>
              {onVoteComment && (
                <button
                  type="button"
                  onClick={handleDownvote}
                  title="Downvote"
                  className="flex items-center gap-0.5 p-0.5 rounded text-muted-foreground hover:text-destructive hover:bg-muted"
                  data-testid="comment-downvote"
                >
                  <ChevronDown className="h-4 w-4" />
                  <span className="text-xs" data-testid="downvote-count">{comment.downvotes ?? 0}</span>
                </button>
              )}
            </div>

            {/* Edit button (own comments only) */}
            {isOwn && onEditComment && (
              <button
                type="button"
                onClick={() => setShowEditForm(true)}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                data-testid="comment-edit-button"
              >
                <Pencil className="h-3 w-3" />
                Edit
              </button>
            )}

            {/* Delete button (own comments only) */}
            {isOwn && onDeleteComment && (
              <button
                type="button"
                onClick={handleDelete}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive"
                data-testid="comment-delete-button"
              >
                <Trash2 className="h-3 w-3" />
                Delete
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Typing indicator component
 */
function TypingIndicator({ names }: { names: string[] }) {
  if (names.length === 0) return null;

  const text =
    names.length === 1
      ? `${names[0]} is typing...`
      : names.length === 2
        ? `${names[0]} and ${names[1]} are typing...`
        : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]} are typing...`;

  return (
    <div
      className="flex items-center gap-2 px-4 py-2 text-sm text-muted-foreground"
      data-testid="typing-indicator"
      role="status"
      aria-live="polite"
      aria-label={text}
    >
      <div className="flex gap-1" aria-hidden="true">
        <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
        <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
        <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
      </div>
      <span>{text}</span>
    </div>
  );
}

/**
 * Offline banner component
 */
function OfflineBanner() {
  return (
    <div
      className="flex items-center justify-center gap-2 px-4 py-2 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 text-sm"
      data-testid="offline-banner"
    >
      <AlertCircle className="h-4 w-4" />
      <span>Connection lost. Reconnecting...</span>
    </div>
  );
}

/**
 * Empty state component
 */
function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <MessageSquare className="h-12 w-12 text-muted-foreground/50 mb-4" />
      <h3 className="text-lg font-medium text-foreground mb-2">No comments yet</h3>
      <p className="text-sm text-muted-foreground max-w-sm">
        Be the first to contribute to this discussion. Share your thoughts and ideas.
      </p>
    </div>
  );
}

/**
 * DiscussionThread - Main thread view component
 *
 * Displays a governance discussion thread with:
 * - Sorted comments from props or discussionStore
 * - Real-time WebSocket updates (when autoSubscribe=true)
 * - Agent vs human comment distinction
 * - Retracted comment markers
 * - Typing indicators
 * - Offline status banner
 *
 * @example
 * ```tsx
 * // With auto-subscription to store
 * <DiscussionThread threadId="discussion-123" />
 *
 * // With explicit props (for testing or custom data)
 * <DiscussionThread
 *   threadId="discussion-123"
 *   comments={comments}
 *   typingUsers={['Alice']}
 *   isConnected={true}
 *   autoSubscribe={false}
 * />
 * ```
 */
export function DiscussionThread({
  threadId,
  comments: propComments,
  typingUsers: propTypingUsers,
  isConnected: propIsConnected,
  className = '',
  autoSubscribe = true,
  userId = '',
  userDisplayName = '',
  currentUserId,
  onVoteComment,
  onEditComment,
  onDeleteComment,
}: DiscussionThreadProps) {
  // Use props if provided, otherwise use store
  const storeComments = useStore($sortedComments);
  const storeIsConnected = useStore($isDiscussionConnected);
  const storeTypingNames = useStore($typingParticipantNames);

  const comments = propComments ?? storeComments;
  const isConnected = propIsConnected ?? storeIsConnected;
  const typingNames = propTypingUsers ?? storeTypingNames;

  // Subscribe to thread on mount (only if autoSubscribe is true)
  useEffect(() => {
    if (autoSubscribe && threadId && userId && userDisplayName) {
      subscribeThread(threadId, userId, userDisplayName);
    }

    return () => {
      if (autoSubscribe && threadId) {
        unsubscribeThread(threadId);
      }
    };
  }, [threadId, autoSubscribe, userId, userDisplayName]);

  return (
    <div className={`flex flex-col h-full ${className}`}>
      {/* Offline banner */}
      {!isConnected && <OfflineBanner />}

      {/* Comments list */}
      <div className="flex-1 overflow-y-auto">
        {comments.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="flex flex-col gap-3 p-4">
            {comments.map((comment) => (
              <CommentItem
                key={comment.id}
                comment={comment}
                currentUserId={currentUserId}
                onVoteComment={onVoteComment}
                onEditComment={onEditComment}
                onDeleteComment={onDeleteComment}
              />
            ))}
          </div>
        )}

        {/* Typing indicator */}
        <TypingIndicator names={typingNames} />
      </div>
    </div>
  );
}

export default DiscussionThread;
